import { useState } from 'react';
import { itemName, rarityLabel } from '@/i18n';
import { getItem } from '@/domain/items';
import { upgradedStats } from '@/domain/upgrade';
import type { EquipmentInstance, Rarity } from '@/domain/types';
import { ItemIcon } from './ItemIcon';
import './CreateListingModal.css';

interface CreateListingModalProps {
  instance: EquipmentInstance;
  onClose: () => void;
  onConfirm: (price: number) => Promise<{ ok: boolean; error?: string }>;
}

const RARITY_COLOR: Record<Rarity, string> = {
  common: '#a4b8c8',
  uncommon: '#4affb0',
  rare: '#4aa7ff',
  epic: '#c64aff',
  legendary: '#ff9a2b',
  mythic: '#ff4af0',
};

/** Suggested price formula: baseValue × rarity-multiplier + upgrade bonus */
function suggestPrice(baseValue: number, rarity: Rarity, upgradeLevel: number): number {
  const rarityMult: Record<Rarity, number> = {
    common: 1, uncommon: 1.6, rare: 3, epic: 6, legendary: 12, mythic: 30,
  };
  return Math.max(10, Math.floor(baseValue * rarityMult[rarity] + upgradeLevel * baseValue * 0.3));
}

export function CreateListingModal({ instance, onClose, onConfirm }: CreateListingModalProps) {
  const def = getItem(instance.itemId);
  if (!def) return null;
  const color = RARITY_COLOR[def.rarity];
  const stats = def.stats ? upgradedStats(def.stats, instance.upgradeLevel) : null;

  const suggested = suggestPrice(def.baseValue, def.rarity, instance.upgradeLevel);
  const [priceStr, setPriceStr] = useState(String(suggested));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const price = Math.floor(Number(priceStr));
  const isValidPrice = Number.isFinite(price) && price >= 1 && price <= 1_000_000_000;

  const submit = async () => {
    if (!isValidPrice) {
      setError('Ungültiger Preis (1 – 1.000.000.000)');
      return;
    }
    setBusy(true);
    setError(null);
    const result = await onConfirm(price);
    setBusy(false);
    if (result.ok) {
      onClose();
    } else {
      setError(`Listing fehlgeschlagen: ${result.error ?? 'unbekannt'}`);
    }
  };

  return (
    <div className="create-listing__overlay" onClick={onClose}>
      <div className="create-listing" onClick={(e) => e.stopPropagation()}>
        <div className="create-listing__header" style={{ borderColor: color }}>
          <ItemIcon itemId={def.id} sizeRem={2.8} />
          <div>
            <div className="create-listing__name">
              {itemName(def.nameKey)} <span style={{ color }}>+{instance.upgradeLevel}</span>
            </div>
            <div className="create-listing__rarity" style={{ color }}>
              {rarityLabel(def.rarity)} · {def.equipSlot}
            </div>
            {stats && (
              <div className="create-listing__stats">
                {stats.attack > 0 && <span>⚔ {stats.attack}</span>}
                {stats.defense > 0 && <span>🛡 {stats.defense}</span>}
                {stats.health > 0 && <span>♥ {stats.health}</span>}
              </div>
            )}
          </div>
        </div>

        <div className="create-listing__price-section">
          <label className="create-listing__label">
            <span>Verkaufspreis (◈ Credits)</span>
            <input
              type="number"
              inputMode="numeric"
              min="1"
              value={priceStr}
              onChange={(e) => setPriceStr(e.target.value)}
              autoFocus
            />
          </label>
          <div className="create-listing__suggest">
            Vorschlag: <button
              className="link-btn"
              type="button"
              onClick={() => setPriceStr(String(suggested))}
            >
              ◈ {suggested.toLocaleString('de-DE')}
            </button>
          </div>
          <div className="create-listing__fee">
            Marktgebühr: 5% · Du erhältst <strong>◈ {Math.floor((price || 0) * 0.95).toLocaleString('de-DE')}</strong>
          </div>
        </div>

        {error && <div className="create-listing__error">⛔ {error}</div>}

        <div className="create-listing__actions">
          <button
            className="btn btn--primary"
            onClick={submit}
            disabled={!isValidPrice || busy}
          >
            {busy ? 'Listet …' : 'Listen'}
          </button>
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            ← Zurück
          </button>
        </div>
      </div>
    </div>
  );
}
